import type { FC, ReactNode } from "react";
import { FirstierLogo } from "@/components/foundations/logo/firstier-logo";
import { ShieldAccountCard } from "@/components/shield/account-card";
import { cx } from "@/utils/cx";
import { MobileNavigationHeader } from "../base-components/mobile-header";
import { NavItemBase } from "../base-components/nav-item";
import { NavList } from "../base-components/nav-list";
import type { NavItemType } from "../config";

export interface NavGroup {
    label: string;
    items: NavItemType[];
    /** Optional icon, shown on the summary row when the group is collapsible. */
    icon?: FC<{ className?: string }>;
    /** Render as an expandable `<details>` accordion instead of a subheading section. */
    collapsible?: boolean;
}

interface SidebarNavigationProps {
    /** URL of the currently active item. */
    activeUrl?: string;
    /** List of items to display — flat array or grouped with section labels. */
    items: NavItemType[] | NavGroup[];
    /** List of footer items to display. */
    footerItems?: NavItemType[];
    /** Feature card to display. */
    featureCard?: ReactNode;
    /** Whether to show the account card. */
    showAccountCard?: boolean;
    /** Whether to hide the right side border. */
    hideBorder?: boolean;
    /** Additional CSS classes to apply to the sidebar. */
    className?: string;
    /** Whether to round the account card avatar. */
    avatarRounded?: boolean;
}

function isGrouped(items: NavItemType[] | NavGroup[]): items is NavGroup[] {
    return items.length > 0 && "items" in items[0];
}

export const SidebarNavigationSimple = ({
    activeUrl,
    items,
    footerItems = [],
    featureCard,
    showAccountCard = true,
    hideBorder = false,
    className,
}: SidebarNavigationProps) => {
    const MAIN_SIDEBAR_WIDTH = 280;

    const content = (
        <aside
            style={
                {
                    "--width": `${MAIN_SIDEBAR_WIDTH}px`,
                } as React.CSSProperties
            }
            className={cx(
                "dark-mode flex h-full w-full max-w-full flex-col justify-between overflow-auto bg-primary pt-4 lg:w-(--width) lg:pt-5",
                !hideBorder && "border-secondary md:border-r",
                className,
            )}
        >
            <div className="flex flex-col gap-5 px-4 lg:px-5">
                <div className="flex">
                    <FirstierLogo className="h-7 w-auto" />
                </div>

            </div>

            {isGrouped(items) ? (
                <ul className="mt-5 flex flex-col">
                    {items.slice(0, -1).map((group) =>
                        group.collapsible ? (
                            <li key={group.label} className="px-4 pt-3">
                                <details className="appearance-none" open={group.items.some((item) => item.href === activeUrl)}>
                                    <NavItemBase icon={group.icon} type="collapsible">
                                        {group.label}
                                    </NavItemBase>
                                    <dd>
                                        <ul className="pb-1">
                                            {group.items.map((item) => (
                                                <li key={item.label} className="py-0.25">
                                                    <NavItemBase href={item.href} badge={item.badge} type="collapsible-child" current={item.href === activeUrl}>
                                                        {item.label}
                                                    </NavItemBase>
                                                </li>
                                            ))}
                                        </ul>
                                    </dd>
                                </details>
                            </li>
                        ) : (
                            <li key={group.label}>
                                <div className="px-5 pb-1 pt-3 first:pt-0">
                                    <p className="text-xs font-bold uppercase text-quaternary">{group.label}</p>
                                </div>
                                <ul className="px-4 pb-2">
                                    {group.items.map((item) => (
                                        <li key={item.label} className="py-0.25">
                                            <NavItemBase icon={item.icon} href={item.href} badge={item.badge} type="link" current={item.href === activeUrl}>
                                                {item.label}
                                            </NavItemBase>
                                        </li>
                                    ))}
                                </ul>
                            </li>
                        ),
                    )}
                </ul>
            ) : (
                <NavList activeUrl={activeUrl} items={items} />
            )}

            <div className="mt-auto flex flex-col gap-3 px-4 py-4 lg:py-5">
                {isGrouped(items) && items.length > 1 && (() => {
                    const lastGroup = items[items.length - 1];
                    return (
                        <div>
                            <div className="px-1 pb-1">
                                <p className="text-xs font-bold uppercase text-quaternary">{lastGroup.label}</p>
                            </div>
                            <ul>
                                {lastGroup.items.map((item) => (
                                    <li key={item.label} className="py-0.25">
                                        <NavItemBase icon={item.icon} href={item.href} badge={item.badge} type="link" current={item.href === activeUrl}>
                                            {item.label}
                                        </NavItemBase>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    );
                })()}

                {footerItems.length > 0 && (
                    <ul className="flex flex-col">
                        {footerItems.map((item) => (
                            <li key={item.label} className="py-px">
                                <NavItemBase badge={item.badge} icon={item.icon} href={item.href} type="link" current={item.href === activeUrl}>
                                    {item.label}
                                </NavItemBase>
                            </li>
                        ))}
                    </ul>
                )}

                {featureCard}

                {showAccountCard && <ShieldAccountCard />}
            </div>
        </aside>
    );

    return (
        <>
            {/* Mobile header navigation */}
            <MobileNavigationHeader>{content}</MobileNavigationHeader>

            {/* Desktop sidebar navigation */}
            <div className="hidden lg:fixed lg:inset-y-0 lg:left-0 lg:flex">{content}</div>

            {/* Placeholder to take up physical space because the real sidebar has `fixed` position. */}
            <div
                style={{
                    paddingLeft: MAIN_SIDEBAR_WIDTH,
                }}
                className="invisible hidden lg:sticky lg:top-0 lg:bottom-0 lg:left-0 lg:block"
            />
        </>
    );
};
